/**
 * Maps backend native-auth error slugs (see NativeAuthError.php) to i18n message
 * keys, so the SPA shows its own localized text and never the raw Microsoft
 * error. Unknown slugs fall back to the generic message.
 *
 * Uses the app-wide react-intl catalogue (AUTH.NATIVE.* keys in the shared
 * message files) — the native-auth screens share the single i18n system.
 */
import {IntlShape} from 'react-intl';
import axios from 'axios';

const GENERIC_KEY = 'AUTH.NATIVE.ERROR.GENERIC';

const CODE_TO_KEY: Record<string, string> = {
  invalid_credentials: 'AUTH.NATIVE.ERROR.INVALID_CREDENTIALS',
  invalid_otp: 'AUTH.NATIVE.ERROR.INVALID_OTP',
  session_expired: 'AUTH.NATIVE.ERROR.SESSION_EXPIRED',
  flow_expired: 'AUTH.NATIVE.ERROR.FLOW_EXPIRED',
  otp_resend_failed: 'AUTH.NATIVE.ERROR.OTP_RESEND_FAILED',
  reset_start_failed: 'AUTH.NATIVE.ERROR.RESET_START_FAILED',
  password_too_weak: 'AUTH.NATIVE.ERROR.PASSWORD_TOO_WEAK',
  password_too_short: 'AUTH.NATIVE.ERROR.PASSWORD_TOO_SHORT',
  password_too_long: 'AUTH.NATIVE.ERROR.PASSWORD_TOO_LONG',
  password_recently_used: 'AUTH.NATIVE.ERROR.PASSWORD_RECENTLY_USED',
  password_banned: 'AUTH.NATIVE.ERROR.PASSWORD_BANNED',
  password_invalid: 'AUTH.NATIVE.ERROR.PASSWORD_INVALID',
  generic: GENERIC_KEY,
};

/** Client-side validation (two password fields don't match). */
export const PASSWORD_MISMATCH_KEY = 'AUTH.NATIVE.ERROR.PASSWORD_MISMATCH';

/** Pull the backend error slug out of an axios error (falls back to 'generic'). */
export const extractErrorCode = (err: unknown): string => {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as {code?: string} | undefined)?.code ?? 'generic';
  }
  return 'generic';
};

/** Translate a backend error slug to a localized message. */
export const nativeAuthErrorMessage = (intl: IntlShape, code?: string): string =>
  intl.formatMessage({id: (code && CODE_TO_KEY[code]) || GENERIC_KEY});
